import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Row, Col, InputGroup } from "react-bootstrap";
import axios from "axios";
import config from "../../../config";
import { toast } from "react-toastify";

const AddInputModal = ({ show, handleClose, batchId, refreshBatch, bomId }) => {
  const [bom, setBom] = useState(null);
  const [locations, setLocations] = useState([]);
  const [poLotsForLocation, setPoLotsForLocation] = useState([]); // Renamed from poLots to avoid confusion
  const [formData, setFormData] = useState({
    materialId: "",
    quantity: "",
    unit: "",
    locationId: "",
    poId: "",
    packingMaterialId: "",
    quantityPerBag: "",
    numberOfBags: "",
    outerPackingMaterialId: "",
    outerNumberOfBags: "",
    selectedPoLotDetails: null, // To store the available options for the selected poLot
  });

  useEffect(() => {
    if (show && bomId) {
      fetchBom(bomId); // Fetch the BOM data based on passed bomId
    }
  }, [show, bomId]);

  const fetchBom = async (bomId) => {
    try {
      const res = await axios.get(`${config.API_BASE_URL}/process-definitions/${bomId}`);
      setBom(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchLocations = async (productId) => {
    try {
      const res = await axios.get(`${config.API_BASE_URL}/new-warehouses/storage/products?productId=${productId}`);
      setLocations(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const handleMaterialChange = async (materialId) => {
    const selectedMaterial = bom?.rawMaterials.find((rm) => rm.materialId._id === materialId);
    setFormData((prev) => ({
      ...prev,
      materialId,
      unit: selectedMaterial?.unit || "", // Prefill unit based on material selection
      locationId: "",
      poId: "",
      packingMaterialId: "",
      quantityPerBag: "",
      numberOfBags: "",
      outerPackingMaterialId: "",
      outerNumberOfBags: "",
      selectedPoLotDetails: null,
      quantity: "", // Reset quantity
    }));
    // Fetch available locations for the selected material
    if (materialId) {
      fetchLocations(materialId);
    }
  };

  const handleLocationChange = async (locationId) => {
    const selectedLocation = locations.find((loc) => loc._id === locationId);
    setPoLotsForLocation(selectedLocation?.poLots || []);
    // Update available PO and restrict quantity based on available quantity in the selected PO
    setFormData((prev) => ({
      ...prev,
      locationId,
      poId: "", // Reset PO selection
      packingMaterialId: "",
      quantityPerBag: "",
      numberOfBags: "",
      outerPackingMaterialId: "",
      outerNumberOfBags: "",
      selectedPoLotDetails: null,
      quantity: "", // Reset quantity
    }));
  };

  const handlepoLotChange = async (poId) => {
    const selectedPoLotData = poLotsForLocation.find((lot) => lot.poId === poId);
    
    setFormData((prev) => ({
      ...prev,
      poId,
      selectedPoLotDetails: selectedPoLotData,
      packingMaterialId: selectedPoLotData?.packingMaterial?._id || "",
      quantityPerBag: selectedPoLotData?.quantityPerBag || "",
      numberOfBags: "", // Reset number of bags when a new PO lot is selected
      outerPackingMaterialId: selectedPoLotData?.outerPackingMaterial?._id || "",
      outerNumberOfBags: "", // Reset outer number of bags
      quantity: "", // Reset quantity
    }));
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => {
      const updatedForm = { ...prev, [field]: value };

      if (field === "numberOfBags" && updatedForm.selectedPoLotDetails?.packingMaterial && updatedForm.quantityPerBag) {
        updatedForm.quantity = parseFloat(value || 0) * parseFloat(updatedForm.quantityPerBag);
      } else if (field === "quantity" && !updatedForm.selectedPoLotDetails?.packingMaterial) {
        // If not packed, allow direct quantity input
        updatedForm.quantity = value;
      }
      return updatedForm;
    });
  };

  const handleSubmit = async () => {
    try {
      const payload = {
        ...formData,
        packingMaterialId: formData.packingMaterialId || null,
        quantityPerBag: formData.quantityPerBag === "" ? null : formData.quantityPerBag,
        numberOfBags: formData.numberOfBags === "" ? null : formData.numberOfBags,
        outerPackingMaterialId: formData.outerPackingMaterialId || null,
        outerNumberOfBags: formData.outerNumberOfBags === "" ? null : formData.outerNumberOfBags,
        poId: formData.poId || null,
      };
      // Filter out temporary `selectedPoLotDetails` before sending to backend
      delete payload.selectedPoLotDetails;

      await axios.post(`${config.API_BASE_URL}/production-batches/${batchId}/inputs`, payload);
      toast.success("Input material added successfully");
      handleClose();
      refreshBatch();
    } catch (err) {
      toast.error("Failed to add input material");
      console.error(err);
    }
  };

  const selectedPoLot = formData.selectedPoLotDetails;

  return (
    <Modal show={show} onHide={handleClose}>
      <Modal.Header closeButton>
        <Modal.Title>Add Input Material</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {bom ? (
          <Form>
            <Row className="mb-3">
              <Col>
                <Form.Label>Material</Form.Label>
                <Form.Select
                  value={formData.materialId}
                  onChange={(e) => handleMaterialChange(e.target.value)}
                >
                  <option value="">-- Select Material --</option>
                  {bom.rawMaterials.map((rm) => (
                    <option key={rm.materialId._id} value={rm.materialId._id}>
                      {rm.materialId.name || rm.materialId.productName}
                    </option>
                  ))}
                </Form.Select>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col>
                <Form.Label>Warehouse Location</Form.Label>
                <Form.Select
                  value={formData.locationId}
                  onChange={(e) => handleLocationChange(e.target.value)}
                >
                  <option value="">-- Select Location --</option>
                  {locations.map((loc) => (
                    <option key={loc._id} value={loc._id}>
                      {loc.fullPath}
                    </option>
                  ))}
                </Form.Select>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col>
                <Form.Label>PO/Vendor & Packing</Form.Label>
                <Form.Select
                  value={formData.poId}
                  onChange={(e) => handlepoLotChange(e.target.value)}
                  disabled={!formData.locationId}
                >
                  <option value="">-- Select PO/Packing --</option>
                  {poLotsForLocation.map((lot) => (
                    <option key={`${lot.poId}-${lot.packingMaterial?._id || 'unpacked'}-${lot.outerPackingMaterial?._id || 'no_outer'}`} value={lot.poId}>
                      {lot.purchaseOrder.poNumber} - {lot.purchaseOrder.vendor}
                      {lot.packingMaterial ? ` (${lot.packingMaterial.name}, ${lot.quantityPerBag} ${formData.unit}/bag, ${lot.availableBags} bags)` : ' (Unpacked)'}
                      {lot.outerPackingMaterial ? ` (Outer: ${lot.outerPackingMaterial.name}, ${lot.availableOuterBags} outer bags)` : ''}
                      (Available: {lot.availableQuantity} {formData.unit})
                    </option>
                  ))}
                </Form.Select>
              </Col>
            </Row>

            {selectedPoLot?.packingMaterial ? (
                <>
                    <Row className="mb-3">
                        <Col md={6}>
                            <Form.Label>Quantity Per Bag</Form.Label>
                            <InputGroup>
                                <Form.Control
                                    type="number"
                                    value={formData.quantityPerBag}
                                    readOnly
                                />
                                {formData.unit && (
                                    <InputGroup.Text>{formData.unit}</InputGroup.Text>
                                )}
                            </InputGroup>
                        </Col>
                        <Col md={6}>
                            <Form.Label>Number of Bags</Form.Label>
                            <InputGroup>
                                <Form.Control
                                    type="number"
                                    value={formData.numberOfBags}
                                    min={0}
                                    max={selectedPoLot?.availableBags || ""}
                                    placeholder={selectedPoLot?.availableBags ? `Max ${selectedPoLot.availableBags}` : ""}
                                    onChange={(e) => handleInputChange("numberOfBags", e.target.value)}
                                />
                                <InputGroup.Text>Bags</InputGroup.Text>
                            </InputGroup>
                        </Col>
                    </Row>
                    {selectedPoLot?.outerPackingMaterial && (
                      <Row className="mb-3">
                        <Col md={6}>
                          <Form.Label>Outer Packing Material</Form.Label>
                          <Form.Control type="text" readOnly value={selectedPoLot.outerPackingMaterial.name} />
                        </Col>
                        <Col md={6}>
                          <Form.Label>Number of Outer Bags</Form.Label>
                          <InputGroup>
                            <Form.Control
                              type="number"
                              value={formData.outerNumberOfBags}
                              min={0}
                              max={selectedPoLot?.availableOuterBags || ""}
                              placeholder={selectedPoLot?.availableOuterBags ? `Max ${selectedPoLot.availableOuterBags}` : ""}
                              onChange={(e) => handleInputChange("outerNumberOfBags", e.target.value)}
                            />
                            <InputGroup.Text>Outer Bags</InputGroup.Text>
                          </InputGroup>
                        </Col>
                      </Row>
                    )}
                </>
            ) : null}

            <Row className="mb-3">
              <Col md={12}>
                <Form.Label>Quantity</Form.Label>
                <div className="position-relative">
                  <Form.Control
                    type="number"
                    value={formData.quantity}
                    min={0}
                    max={selectedPoLot ? selectedPoLot.availableQuantity : ""}
                    placeholder={selectedPoLot ? `Max ${selectedPoLot.availableQuantity}` : ""}
                    onChange={(e) => handleInputChange("quantity", e.target.value)}
                    readOnly={!!selectedPoLot?.packingMaterial} // Read-only if packed
                  />
                  <span className="unit-suffix">{formData.unit}</span>
                </div>
              </Col>
            </Row>
          </Form>
        ) : (
          <p>Loading BOM data...</p>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSubmit}>
          Add
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default AddInputModal;
